<template>
  <div class="ai-suggestion-banner" data-cy="ai-suggestion-banner">
    <div class="banner-content">
      <div class="banner-message" role="status" aria-live="polite">
        <template v-if="jobStatus">
          <strong>AI sizing: {{ jobStatus }}</strong>
          <p>You can return to this scenario to check progress or review results. Queued work waits for capacity; running calculations have execution limits.</p>
        </template>
        <span v-else>{{ message }}</span>
      </div>
      <Button
        class="populate-button"
        severity="success"
        data-cy="populate-design-button"
        :disabled="disabled"
        @click="handlePopulateClick"
      >
        <icon :icon="['fas', 'sparkles']" class="mr-2" />
        {{ jobStatus ? 'View AI sizing status / results' : buttonLabel }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import Button from "primevue/button";

withDefaults(
  defineProps<{
    message?: string;
    buttonLabel?: string;
    disabled?: boolean;
    jobStatus?: string;
  }>(),
  {
    message:
      "Generate an AI-suggested design, then review the equipment and calculated performance.",
    buttonLabel: "Generate Design",
  },
);

const emit = defineEmits<{
  (e: "populate-design"): void;
}>();

function handlePopulateClick() {
  emit("populate-design");
}
</script>

<style lang="scss" scoped>
.ai-suggestion-banner {
  border: 2px solid var(--green-500);
  background-color: rgba(34, 197, 94, 0.08);
  border-radius: 6px;
  padding: 1rem 1.5rem;
  margin-left: 3em;
  margin-right: 1em;
  margin-bottom: 1.5rem;
  margin-top: 1rem;
  animation: slideDown 0.3s ease-out;
}

.banner-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.banner-message {
  flex: 1;
  color: var(--text-color);
  font-size: 0.95rem;
  line-height: 1.5;
  p { margin: 0.25rem 0 0; }
}

.populate-button {
  flex-shrink: 0;
  white-space: nowrap;

  :deep(.p-button-icon) {
    margin-right: 0.5rem;
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 750px) {
  .ai-suggestion-banner {
    margin-left: 1em;
    margin-right: 1em;
  }

  .banner-content {
    flex-direction: column;
    align-items: stretch;
  }

  .populate-button {
    width: 100%;
  }
}
</style>
